<script setup lang="ts">
import { debounce } from 'lodash-es'
import { langList } from '@/locales/index'
import { langModuleNameMap } from '@/format/lang'
import { useLanguage } from '@/composition'

defineOptions({ name: 'LanguageSwitch' })
const props = defineProps<{
  /**
   * 是否显示语言切换下拉框
   */
  visible: boolean
  /**
   * 是否透明主题
   */
  isTransparentTheme: boolean
  /**
   * 点击事件处理方法
   */
  onClick: (e: MouseEvent, visible?: boolean) => void
}>()
const { curLanguage, changeLanguage } = useLanguage()
const cloneCurLanguage = unref(curLanguage.value) // 克隆的当前语言，用于判断是否切换了语言

/**
 * 打开语言切换下拉框
 */
const openLangDropdown = debounce((e: MouseEvent) => props.onClick(e), 200, { leading: true, trailing: false })
/**
 * 关闭语言切换下拉框
 */
const closeLangDropdown = debounce(
  (e: MouseEvent) => {
    if (!props.visible) return
    props.onClick(e, false)
  },
  500,
  { leading: true, trailing: false }
)

const transparentThemeColor = computed(() =>
  props.isTransparentTheme ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 0.8)'
)

const langOptions = computed(() => unref(langList).filter((item) => item.content !== undefined))

/**
 * 处理下拉框显示状态变化
 */
const handleDropdownVisibleChange = (visible: boolean) => {
  if (!visible && cloneCurLanguage !== unref(curLanguage.value)) {
    nextTick(() => {
      window.location.reload()
    })
  }
}

/**
 * 切换语言
 */
const changeLang = async (lang: string) => {
  changeLanguage(lang)
}
</script>

<template>
  <div id="languageSwitchComp" v-click-outside="closeLangDropdown" class="language-switch" @click="openLangDropdown">
    <TcomIcon class="iternationality-icon" name="internationality" :size="24" :color="transparentThemeColor" />
    <TcomDropdown
      trigger="click"
      placement="bottom-right"
      :min-column-width="136"
      :max-column-width="136"
      :popup-props="{
        overlayClassName: 'language-switch-dropdown',
        attach: '#languageSwitchComp',
        visible,
        onVisibleChange: handleDropdownVisibleChange
      }"
    >
      <TcomButton theme="default" variant="text" size="large">
        {{ langModuleNameMap[curLanguage] }}
        <template #suffix>
          <TcomIcon :color="transparentThemeColor">
            <TcomTIcon
              :class="['chevron-down-icon', visible && 'chevron-down-icon--active']"
              t-icon-name="ChevronDownIcon"
            />
          </TcomIcon>
        </template>
      </TcomButton>
      <TcomDropdownMenu>
        <TcomDropdownItem
          v-for="(option, i) in langOptions"
          :key="i"
          :class="['lang-item', curLanguage !== option.value && 'lang-item--unactive']"
          :value="option.value"
          :active="curLanguage === option.value"
          @click="() => changeLang(option.value as string)"
        >
          <span class="text">{{ option.content }}</span>
          <TcomIcon v-show="curLanguage === option.value" color="rgba(58, 191, 200, 1)" :size="16">
            <TcomTIcon class="check-icon" t-icon-name="CheckIcon" />
          </TcomIcon>
        </TcomDropdownItem>
      </TcomDropdownMenu>
    </TcomDropdown>
  </div>
</template>

<style scoped lang="less">
.language-switch {
  .flex(row, center, center);
  margin-right: 40px;
  height: 24px;
  cursor: pointer;

  .iternationality-icon {
    margin-right: 4px;
  }
  .chevron-down-icon {
    transition: transform 0.3s;
    &--active {
      transform: rotate(-180deg);
    }
  }
  :deep(.t-button) {
    &__text {
      font-size: 18px;
    }
    &.t-size-l {
      .t-icon {
        font-size: 16px;
      }
    }
    &:not(.t-is-disabled):not(.t-button--ghost) {
      --ripple-color: transparent;
    }
    &--variant-text {
      padding-left: 0;
      padding-right: 0;
      &:hover {
        background-color: transparent;
        border-color: transparent;
      }
    }
    &__text {
      color: v-bind(transparentThemeColor);
    }
    &__suffix {
      margin-left: 4px;
    }
  }

  :deep(.language-switch-dropdown) {
    &.t-popup[data-popper-placement^='bottom'] {
      .t-popup__content {
        margin-top: 28px;
      }
    }
    .t-dropdown {
      box-shadow:
        0px 5px 5px -3px rgba(0, 0, 0, 0.1),
        0px 8px 10px 1px rgba(0, 0, 0, 0.06),
        0px 3px 14px 2px rgba(0, 0, 0, 0.05);
      &__menu {
        padding: var(--td-pop-padding-xl);
        border-radius: 8px;
        & > div:not(:first-child) {
          margin-top: 4px;
        }
      }

      &__item {
        box-sizing: border-box;
        padding: 7px 8px;
        max-width: 136px !important;
        border-radius: var(--td-radius-medium);
        &:hover {
          background-color: rgba(243, 243, 243, 1);
        }
        &--theme-default {
          color: rgba(0, 0, 0, 0.8);
        }
        &--active {
          color: rgba(58, 191, 200, 1);
          background-color: transparent;
        }
        &-text {
          .flex(row, space-between, center);
        }
      }
    }
  }
}
</style>
